import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { taxRatesApi, TaxRate, CreateTaxRateDto } from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';
import { formatDate, formatPercentValue } from '../utils/formatting';
import { parseRateInput } from '../utils/tenantSettings';

export const TaxRates: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate | null>(null);
  const [formData, setFormData] = useState<CreateTaxRateDto>({
    tax_rate_name: '',
    rate_percentage: 0,
    is_active: true,
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchTaxRates();
  }, [token]);

  const fetchTaxRates = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await taxRatesApi.getAll(token);
      setTaxRates(data);
      setError('');
    } catch (err) {
      setError(t('taxRates.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTaxRate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setFormLoading(true);
    setFormError('');

    try {
      await taxRatesApi.create(token, formData);
      setShowCreateModal(false);
      setFormData({ tax_rate_name: '', rate_percentage: 0, is_active: true });
      await fetchTaxRates();
    } catch (err: any) {
      setFormError(err.message || t('taxRates.failedToCreate'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTaxRate = async () => {
    if (!token || !selectedTaxRate) return;

    setFormLoading(true);
    setFormError('');

    try {
      await taxRatesApi.delete(token, selectedTaxRate.tax_rate_id);
      setShowDeleteModal(false);
      setSelectedTaxRate(null);
      await fetchTaxRates();
    } catch (err: any) {
      setFormError(err.message || t('taxRates.failedToDelete'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (taxRate: TaxRate) => {
    if (!token) return;

    try {
      if (taxRate.is_active) {
        await taxRatesApi.deactivate(token, taxRate.tax_rate_id);
      } else {
        await taxRatesApi.activate(token, taxRate.tax_rate_id);
      }
      await fetchTaxRates();
    } catch (err: any) {
      setError(err.message || t('taxRates.failedToUpdate'));
    }
  };

  const openDeleteModal = (taxRate: TaxRate) => {
    setSelectedTaxRate(taxRate);
    setFormError('');
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    setFormData({ tax_rate_name: '', rate_percentage: 0, is_active: true });
    setFormError('');
    setShowCreateModal(true);
  };

  return (
    <AdminLayout title={t('taxRates.title')} subtitle={t('taxRates.subtitle')}>
      <div className="">
        {/* Header with Create Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-[var(--navy)] text-white px-4 py-2 rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            {t('taxRates.createTaxRate')}
          </button>
        </div>

        {/* Loading/Error States */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block w-10 h-10 border-3 border-gray-200 border-t-[var(--gold)] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500">{t('taxRates.loadingTaxRates')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden" style={{ background: 'var(--surface)', boxShadow: 'var(--shadow)' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead style={{ borderBottom: '1px solid var(--border)', background: 'var(--gold-pale)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('taxRates.ratePercentage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.created')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {taxRates.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      {t('taxRates.noTaxRates')}
                    </td>
                  </tr>
                ) : (
                  taxRates.map((taxRate) => (
                    <tr key={taxRate.tax_rate_id} className="hover:bg-[var(--gold-pale)]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{taxRate.tax_rate_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatPercentValue(taxRate.rate_percentage)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            taxRate.is_active
                              ? 'bg-[var(--success-bg)] text-[var(--success)]'
                              : 'bg-[var(--error-bg)] text-[var(--error)]'
                          }`}
                        >
                          {taxRate.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(taxRate.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleToggleActive(taxRate)}
                          className={`mr-4 ${
                            taxRate.is_active
                              ? 'text-orange-600 hover:text-orange-900'
                              : 'text-green-600 hover:text-green-900'
                          }`}
                        >
                          {taxRate.is_active ? t('apiKeys.deactivate') : t('apiKeys.activate')}
                        </button>
                        <button
                          onClick={() => openDeleteModal(taxRate)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('taxRates.createNewTaxRate')}</h3>
            <form onSubmit={handleCreateTaxRate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('taxRates.taxRateName')} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.tax_rate_name}
                    onChange={(e) => setFormData({ ...formData, tax_rate_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="Germany VAT"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('taxRates.ratePercentageRange')} *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.rate_percentage}
                    onChange={(e) => setFormData({ ...formData, rate_percentage: parseRateInput(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--gold)] focus:border-transparent"
                    placeholder="19.00"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active_create"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 border-[var(--border)] rounded focus:ring-[var(--gold)]"
                  />
                  <label htmlFor="is_active_create" className="ml-2 text-sm text-gray-700">
                    {t('common.active')}
                  </label>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    {t('taxRates.cannotEditNote')}
                  </p>
                </div>
              </div>
              {formError && (
                <div className="mt-4 text-red-600 text-sm">{formError}</div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors"
                  disabled={formLoading}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[var(--navy)] text-white rounded-lg font-medium hover:bg-[var(--navy-light)] transition-colors disabled:opacity-50"
                  disabled={formLoading}
                >
                  {formLoading ? t('common.creating') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedTaxRate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">{t('taxRates.deleteTaxRate')}</h3>
            <p className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: t('taxRates.deleteConfirmation', { name: selectedTaxRate.tax_rate_name }) }} />
            {formError && (
              <div className="mb-4 text-red-600 text-sm">{formError}</div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-[var(--gold-pale)] transition-colors"
                disabled={formLoading}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteTaxRate}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={formLoading}
              >
                {formLoading ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
